import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Calendar, Check, X, Clock, AlertCircle } from 'lucide-react';

export default function LeaveManagement() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('APPLY'); // APPLY, MY_LEAVES, REVIEW
    const [myLeaves, setMyLeaves] = useState([]);
    const [reviewLeaves, setReviewLeaves] = useState([]);

    // Form state
    const [formData, setFormData] = useState({ date: '', reason: '' });
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (activeTab === 'MY_LEAVES') fetchMyLeaves();
        if (activeTab === 'REVIEW') fetchReviewLeaves();
    }, [activeTab]);

    const fetchMyLeaves = () => {
        fetch(`/api/leaves?userId=${user.id || user.uid}`)
            .then(res => res.json())
            .then(data => setMyLeaves(data));
    };

    const fetchReviewLeaves = () => {
        fetch('/api/leaves')
            .then(res => res.json())
            .then(data => {
                const filtered = user.role === 'HOD'
                    ? data.filter(l => l.applicantRole === 'FACULTY')
                    : data;
                setReviewLeaves(filtered);
            });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await fetch('/api/leaves', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...formData, userId: user.id })
        });
        setMessage('Leave applied successfully!');
        setFormData({ date: '', reason: '' });
        setTimeout(() => setMessage(''), 3000);
    };

    const handleAction = async (leaveId, status) => {
        await fetch(`/api/leaves/${leaveId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status })
        });
        fetchReviewLeaves();
    };

    const StatusBadge = ({ status }) => {
        const colors = {
            'PENDING': 'bg-yellow-100 text-yellow-800',
            'APPROVED': 'bg-green-100 text-green-800',
            'REJECTED': 'bg-red-100 text-red-800'
        };
        return (
            <span className={`px-2 py-1 rounded text-xs font-bold ${colors[status] || 'bg-gray-100'}`}>
                {status}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold font-heading text-primary">Leave Management</h1>
                <p className="text-muted-foreground">Apply for leaves and manage requests</p>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border">
                {['FACULTY', 'HOD', 'PRINCIPAL'].includes(user.role) && (
                    <>
                        <button
                            onClick={() => setActiveTab('APPLY')}
                            className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${activeTab === 'APPLY' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        >
                            Apply for Leave
                        </button>
                        <button
                            onClick={() => setActiveTab('MY_LEAVES')}
                            className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${activeTab === 'MY_LEAVES' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        >
                            My Applications
                        </button>
                    </>
                )}

                {['HOD', 'PRINCIPAL'].includes(user.role) && (
                    <button
                        onClick={() => setActiveTab('REVIEW')}
                        className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${activeTab === 'REVIEW' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                        Review Requests
                    </button>
                )}
            </div>

            <div className="py-4">
                {activeTab === 'APPLY' && (
                    <div className="max-w-md bg-card p-6 rounded-xl border border-border">
                        <h2 className="text-lg font-bold mb-4">New Leave Application</h2>
                        {message && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">{message}</div>}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Date</label>
                                <input
                                    type="date"
                                    className="w-full bg-secondary px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-primary"
                                    value={formData.date}
                                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Reason</label>
                                <textarea
                                    className="w-full bg-secondary px-4 py-2 rounded-lg outline-none focus:ring-1 focus:ring-primary h-24"
                                    value={formData.reason}
                                    onChange={e => setFormData({ ...formData, reason: e.target.value })}
                                    required
                                    placeholder="Brief reason for leave..."
                                />
                            </div>
                            <button type="submit" className="w-full bg-primary text-primary-foreground py-2 rounded-lg font-medium hover:bg-primary/90">
                                Submit Application
                            </button>
                        </form>
                    </div>
                )}

                {activeTab === 'MY_LEAVES' && (
                    <div className="grid gap-4">
                        {myLeaves.length === 0 ? <p className="text-muted-foreground">No applications details found.</p> : (
                            myLeaves.map(leave => (
                                <div key={leave.id} className="bg-card p-4 rounded-xl border border-border flex items-center justify-between">
                                    <div>
                                        <p className="font-bold">{leave.date}</p>
                                        <p className="text-sm text-muted-foreground">{leave.reason}</p>
                                    </div>
                                    <StatusBadge status={leave.status} />
                                </div>
                            ))
                        )}
                    </div>
                )}

                {activeTab === 'REVIEW' && (
                    <div className="grid gap-4">
                        {reviewLeaves.length === 0 ? <p className="text-muted-foreground">No pending requests.</p> : (
                            reviewLeaves.map(leave => (
                                <div key={leave.id} className="bg-card p-4 rounded-xl border border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-bold text-lg">{leave.applicantName}</span>
                                            <span className="text-xs bg-secondary px-2 py-0.5 rounded uppercase">{leave.applicantRole}</span>
                                        </div>
                                        <p className="text-sm font-mono text-primary mb-1">Date: {leave.date}</p>
                                        <p className="text-sm text-muted-foreground quote">"{leave.reason}"</p>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {leave.status === 'PENDING' ? (
                                            <>
                                                <button
                                                    onClick={() => handleAction(leave.id, 'APPROVED')}
                                                    className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium"
                                                >
                                                    <Check size={16} /> Approve
                                                </button>
                                                <button
                                                    onClick={() => handleAction(leave.id, 'REJECTED')}
                                                    className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium"
                                                >
                                                    <X size={16} /> Reject
                                                </button>
                                            </>
                                        ) : (
                                            <StatusBadge status={leave.status} />
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
